<script setup lang="ts">
import { computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { UiSwitch, UiText, useApi, useToaster } from '@shopexperts/shared';
import type { AdminExpertDetail } from '@shopexperts/legacy-api';
import ExpertDetailsSection from './ExpertDetailsSection.vue';

const props = defineProps<{ expert: AdminExpertDetail }>();
const api = useApi();
const queryClient = useQueryClient();
const toaster = useToaster();
const flag = 'hide_expert_share_badge';
const hidden = computed(() => props.expert.flags?.includes(flag) ?? false);
const { mutate, isPending } = useMutation({
  mutationFn: ({ expertId, flags }: { expertId: number; flags: string[] }) =>
    api.admin.expertManager.updateFlags({ params: { expertId }, body: { flags } }),
  onSuccess: (result) => {
    queryClient.setQueriesData<AdminExpertDetail>({
      predicate: (query) => query.queryKey.length === 3
        && query.queryKey[0] === 'admin'
        && query.queryKey[1] === 'expert'
        && String(query.queryKey[2]) === String(result.expert_id),
    }, (expert) => expert ? { ...expert, flags: result.flags } : expert);
    toaster.create({ title: 'Flags saved', type: 'success' });
  },
  onError: (error: Error) => {
    toaster.create({ title: `Failed to save flags: ${error.message}`, type: 'error' });
  },
});

function setHidden(value: boolean) {
  if (isPending.value || !props.expert.flags) return;
  const flags = props.expert.flags.filter((name) => name !== flag);
  if (value) flags.push(flag);
  mutate({ expertId: props.expert.id, flags });
}
</script>

<template>
  <ExpertDetailsSection title="Flags">
    <div class="flex flex-col gap-2">
      <UiSwitch
        label="Hide expert share badge"
        :model-value="hidden"
        :disabled="isPending || expert.flags === undefined"
        @update:model-value="setHidden"
      />
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-600"
        text="Hide the “Get a badge for your website” block on this expert’s public profile."
      />
    </div>
  </ExpertDetailsSection>
</template>
